import React, { useEffect, useState, Suspense } from "react";
import Select from "react-select";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { GridView, SvgComponent } from "components";
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { SeguridadManager } from "managers";
import { LogAuditoria } from "models/LogAuditoria";
import { DateHelper, DEFAULT_PAGINATION_VALUES } from "utils";
import SuspenseECF from "components/Suspense/SuspenseECF";
import Exportar from "components/Exportar/Exportar";
import { DateTimePicker } from "components/DateTimePicker/DateTimePicker";
import uuid from "react-uuid";
import LogAuditoriaView from "./LogAuditoriaView";

export default function LogAuditoriaList({ hideLoading, showLoading, isActive }) {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const [showExportar, setShowExportar] = useState(false);
  const [idSec, setIdSec] = useState(undefined);
  const [showCanvas, setShowCanvas] = useState(false);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { handleException } = useExceptionHandler();
  const { cerrarAlertas } = useMessage();
  const [fechaFilterkey, setFechaFilterKey] = useState(uuid());

  const EVENTOS_LIST = [{ value: "", label: "Todos" }, ...LogAuditoria.EVENTO_LIST];

  useEffect(() => {
    if (isActive)
      buscar();

    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where, isActive]);

  const buscar = () => {
    const whereWithPaginationValues = {
      ...where,
      ...pagination,
      FechaInicio: where.RangoFechas?.fechaInicio,
      FechaFin: where.RangoFechas?.fechaFin
    };

    SeguridadManager.listarLogsAuditoria(whereWithPaginationValues)
      .then((res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);

        try {
          for (let log of res.Data) {
            log["EventoLabel"] = LogAuditoria.getEventoLabel(log.Evento);
            log["Fecha"] = DateHelper.formatStringDate(log["Fecha"], true);
          }
        }
        catch (err) { }

        setList(res.Data);
      })
      .catch(handleException)
      .finally(hideLoading);
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prev) => ({ ...prev, IndiceDePagina: pageNumber - 1 }));
    setWhere({ ...where });
  };

  const onReset = () => {
    setFechaFilterKey(uuid())
  };

  const handleClick = (params) => {
    setIdSec(params.IdSec);
    setShowCanvas(true);
  };

  return (
    <>
      <div style={{ paddingRight: "6px" }}>
        <ListHeader
          exportar={() => setShowExportar(true)}
        />
      </div>

      {showExportar && (
        <Suspense fallback={<SuspenseECF />}>
          <Exportar
            PROPIEDADES_EXPORTAR={LogAuditoria.PROPIEDADES_EXPORTAR}
            showExportar={showExportar}
            setShowExportar={setShowExportar}
            showLoading={showLoading}
            hideLoading={hideLoading}
            where={where}
            serialize={true}
            exportarCallBack={SeguridadManager.exportarLogsAuditoria}
          />
        </Suspense>
      )}

      {showCanvas && (
        <Suspense fallback={<SuspenseECF />}>
          <LogAuditoriaView
            showCanvas={showCanvas}
            setShowCanvas={setShowCanvas}
            IdSec={idSec}
            showLoading={showLoading}
            hideLoading={hideLoading}
          />
        </Suspense>
      )}

      <GridView
        pagination={pagination}
        setPagination={setPagination}
        allowPaging={pagination.EsPaginable}
        pageSize={pagination.CantidadPorPagina}
        pageIndex={pagination.IndiceDePagina}
        lastPageNumber={pagination.PaginasTotal}
        dataSource={list}
        onPageIndexChanging={onPageIndexChanging}
        where={where}
        setWhere={setWhere}
        onReset={onReset}
      >
        <GridView.Column
          isField
          width="100px"
          showClearFilter={true}
          bodyRenderer={(row) => (
            <div className="float-right">
              <IconButton onClick={() => handleClick({ IdSec: row.IdSec })}>
                <ToolTip message="Ver cambios">
                  <div>
                    <SvgComponent
                      iconName="view"
                      height="25"
                      width="25"
                    />
                  </div>
                </ToolTip>
              </IconButton>
            </div>
          )}
        />
        <GridView.Column
          headerText="Id"
          dataField="IdSec"
          sortExpression="IdSec"
          width="90px"
        />
        <GridView.Column
          headerText="Tabla"
          dataField="Tabla"
          sortExpression="Tabla"
          width="150px"
        />
        <GridView.Column
          headerText="Id en Tabla"
          dataField="Referencia"
          sortExpression="Referencia"
          width="120px"
        />
        <GridView.Column
          headerText="Usuario"
          dataField="NombreUsuario"
          sortExpression="NombreUsuario"
          width="180px"
        />
        <GridView.Column
          headerText="IP"
          dataField="IP"
          sortExpression="IP"
          width="140px"
        />
        <GridView.Column
          date
          hideFilter
          headerText="Fecha"
          dataField="Fecha"
          sortExpression="Fecha"
          width="230px"
          filterRenderer={(onInputChange) => {
            return (
              <DateTimePicker
                key={fechaFilterkey}
                soloFecha
                esRangoFechas
                esGrid
                usarLabel={false}
                fechaInicioOriginal={where?.RangoFechas?.fechaInicio || ""}
                fechaFinOriginal={where?.RangoFechas?.fechaFin || ""}
                onChange={(e) =>
                  onInputChange({
                    target: { name: "RangoFechas", value: e },
                  })
                }
              />
            );
          }}
        />
        <GridView.Column
          headerText="Movimiento"
          dataField="EventoLabel"
          sortExpression="Evento"
          width="150px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={EVENTOS_LIST}
                value={EVENTOS_LIST.filter(function (option) {
                  return (
                    option.value === (where.Evento ? where.Evento : EVENTOS_LIST[0].value)
                  );
                })}
                name="Evento"
                onChange={(e) =>
                  setWhere({
                    ...where,
                    Evento: e.value
                  })
                }
              />
            );
          }}
        />
      </GridView>
    </>
  );
}
